import { useEffect } from "react";

/**
 * Verdrahtet window.onerror und unhandledrejection mit dem öffentlichen
 * Endpoint `/api/public/error-report`. Bewusst tolerant — Fehler dürfen
 * den Client nicht crashen, doppelte Reports werden über einen kleinen
 * Fingerprint-Cache pro Tab unterdrückt.
 */
const ENDPOINT = "/api/public/error-report";
const recentlySent = new Map<string, number>();
const COOLDOWN_MS = 60_000;

function fingerprint(name: string, message: string, stack: string | null): string {
  const head = (stack ?? "").split("\n").slice(0, 2).join("|");
  return `${name}::${message.slice(0, 200)}::${head}`;
}

function shouldSend(fp: string): boolean {
  const now = Date.now();
  const last = recentlySent.get(fp);
  if (last && now - last < COOLDOWN_MS) return false;
  recentlySent.set(fp, now);
  // GC
  if (recentlySent.size > 50) {
    for (const [k, t] of recentlySent) {
      if (now - t > COOLDOWN_MS) recentlySent.delete(k);
    }
  }
  return true;
}

function send(payload: Record<string, unknown>): void {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {
      /* never throw */
    });
  } catch {
    /* never throw */
  }
}

export function ClientErrorReporter() {
  useEffect(() => {
    function onError(ev: ErrorEvent) {
      const err = ev.error;
      const name = err instanceof Error ? err.name : "WindowError";
      const message = err instanceof Error ? err.message : (ev.message ?? "Unknown error");
      const stack = err instanceof Error ? (err.stack ?? null) : null;
      const fp = fingerprint(name, message, stack);
      if (!shouldSend(fp)) return;
      send({
        errorName: name,
        message: String(message).slice(0, 2000),
        stack: stack ? stack.slice(0, 8000) : null,
        routePath: window.location.pathname,
        userAgent: navigator.userAgent.slice(0, 500),
        severity: "error",
        extra: {
          filename: ev.filename ?? null,
          lineno: ev.lineno ?? null,
          colno: ev.colno ?? null,
        },
      });
    }
    function onRejection(ev: PromiseRejectionEvent) {
      const r = ev.reason;
      const name = r instanceof Error ? r.name : "UnhandledRejection";
      const message = r instanceof Error ? r.message : String(r ?? "(no reason)");
      const stack = r instanceof Error ? (r.stack ?? null) : null;
      const fp = fingerprint(name, message, stack);
      if (!shouldSend(fp)) return;
      send({
        errorName: name,
        message: message.slice(0, 2000),
        stack: stack ? stack.slice(0, 8000) : null,
        routePath: window.location.pathname,
        userAgent: navigator.userAgent.slice(0, 500),
        severity: "error",
      });
    }
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);
  return null;
}
